import React from 'react';
import { motion } from 'framer-motion';
import { Globe, TrendingUp, Mic, Shield, Zap, Server, Lock, RefreshCw, Cpu, Database, ArrowRight, CheckCircle2 } from 'lucide-react';
interface HostingProps {
  setActiveSection?: (val: string) => void;
}
export function Hosting({
  setActiveSection
}: HostingProps) {
  return <div className="pt-48 pb-32 px-6 max-w-7xl mx-auto" data-id="element-219">
      {/* Hero Section */}
      <section className="text-center mb-32" data-id="element-220">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="font-['Archivo_Black'] text-4xl md:text-6xl uppercase mb-6 bg-gradient-to-r from-[#FFD700] to-[#FF0000] bg-clip-text text-transparent" data-id="element-221">
          Pro Hosting
        </motion.h2>
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="text-xl text-gray-400 max-w-2xl mx-auto mb-16" data-id="element-222">
          Powered by Niishcloud enterprise infrastructure. Built for massive
          traffic spikes and elite performance.
        </motion.p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-223">
          {[{
          title: '99.9% Uptime',
          icon: Globe,
          desc: 'Guaranteed availability for your global audience.'
        }, {
          title: 'Unlimited Bandwidth',
          icon: TrendingUp,
          desc: 'Scale without limits, no matter how viral you go.'
        }, {
          title: '24/7 Support',
          icon: Mic,
          desc: 'Expert technical assistance whenever you need it.'
        }].map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + i * 0.1
        }} className="p-10 bg-[#1A1A1A] rounded-2xl border border-white/5 hover:border-[#FFD700]/30 transition-all group" data-id="element-224">
              <item.icon className="text-[#FFD700] mx-auto mb-6 group-hover:scale-110 transition-transform" size={40} data-id="element-225" />
              <h3 className="font-bold uppercase text-xl mb-4" data-id="element-226">{item.title}</h3>
              <p className="text-sm text-gray-500" data-id="element-227">{item.desc}</p>
            </motion.div>)}
        </div>
      </section>

      {/* Infrastructure Section */}
      <section className="mb-32" data-id="element-228">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-229">
          <div className="space-y-8" data-id="element-230">
            <span className="text-[#FFD700] font-bold uppercase tracking-[0.3em] text-xs" data-id="element-231">
              The Niishcloud Edge
            </span>
            <h2 className="font-['Archivo_Black'] text-3xl md:text-5xl uppercase leading-tight" data-id="element-232">
              Enterprise-Grade <br data-id="element-233" />
              <span className="text-white/50" data-id="element-234">Infrastructure</span>
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed" data-id="element-235">
              We don't use shared hosting. Every HipHopServers site runs on
              dedicated, bare-metal resources optimized for high-fidelity media
              and streaming.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-236">
              {[{
              title: 'NVMe Storage',
              icon: Database,
              desc: '10x faster than SSD'
            }, {
              title: 'DDR5 RAM',
              icon: Cpu,
              desc: 'Ultra-low latency'
            }, {
              title: 'Dedicated IP',
              icon: Server,
              desc: 'Better SEO & Email'
            }, {
              title: 'Auto-Scaling',
              icon: Zap,
              desc: 'Handles traffic spikes'
            }].map((feat, i) => <div key={i} className="flex items-start gap-4" data-id="element-237">
                  <div className="p-2 bg-[#FFD700]/10 rounded-lg" data-id="element-238">
                    <feat.icon className="text-[#FFD700]" size={20} data-id="element-239" />
                  </div>
                  <div data-id="element-240">
                    <h4 className="font-bold uppercase text-sm" data-id="element-241">
                      {feat.title}
                    </h4>
                    <p className="text-xs text-gray-500" data-id="element-242">{feat.desc}</p>
                  </div>
                </div>)}
            </div>
          </div>
          <div className="relative" data-id="element-243">
            <div className="aspect-square bg-gradient-to-br from-[#FFD700]/20 to-red-600/10 rounded-3xl border border-white/10 overflow-hidden flex items-center justify-center p-12" data-id="element-244">
              <div className="relative w-full h-full border border-[#FFD700]/20 rounded-full animate-[spin_20s_linear_infinite] flex items-center justify-center" data-id="element-245">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-[#FFD700] rounded-full shadow-[0_0_20px_#FFD700]" data-id="element-246" />
                <div className="w-2/3 h-2/3 border border-white/10 rounded-full flex items-center justify-center" data-id="element-247">
                  <Server size={64} className="text-[#FFD700] animate-pulse" data-id="element-248" />
                </div>
              </div>
            </div>
            {/* Decorative elements */}
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#FFD700]/10 blur-[50px] rounded-full" data-id="element-249" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-red-600/10 blur-[50px] rounded-full" data-id="element-250" />
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="mb-32 p-12 bg-[#1A1A1A] border border-white/5 rounded-3xl relative overflow-hidden" data-id="element-251">
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-12" data-id="element-252">
          <div className="lg:col-span-1" data-id="element-253">
            <h3 className="font-['Archivo_Black'] text-2xl uppercase mb-6" data-id="element-254">
              Military-Grade Security
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-8" data-id="element-255">
              Your content is your empire. We protect it with the same
              technology used by global financial institutions.
            </p>
            <div className="flex items-center gap-4 p-4 bg-black/40 rounded-xl border border-white/5" data-id="element-256">
              <Lock className="text-emerald-500" size={24} data-id="element-257" />
              <div data-id="element-258">
                <p className="text-xs font-bold uppercase text-emerald-500" data-id="element-259">
                  SSL Certificate
                </p>
                <p className="text-[10px] text-gray-500" data-id="element-260">Active & Encrypted</p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-8" data-id="element-261">
            {[{
            title: 'DDoS Protection',
            desc: 'Advanced mitigation layers to keep you online during attacks.',
            icon: Shield
          }, {
            title: 'Daily Backups',
            desc: 'Automated snapshots of your entire site, kept for 30 days.',
            icon: RefreshCw
          }, {
            title: 'Malware Scanning',
            desc: 'Real-time monitoring for suspicious activity and code.',
            icon: Search
          }, {
            title: 'WAF Firewall',
            desc: 'Enterprise Web Application Firewall blocks malicious traffic.',
            icon: Lock
          }].map((item, i) => <div key={i} className="space-y-4" data-id="element-262">
                <item.icon className="text-[#FFD700]" size={28} data-id="element-263" />
                <h4 className="font-bold uppercase text-lg" data-id="element-264">{item.title}</h4>
                <p className="text-sm text-gray-500 leading-relaxed" data-id="element-265">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
        {/* Background glow */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#FFD700]/5 to-transparent pointer-events-none" data-id="element-266" />
      </section>

      {/* Global Network Section */}
      <section className="mb-32 text-center" data-id="element-267">
        <h2 className="font-['Archivo_Black'] text-3xl md:text-5xl uppercase mb-16" data-id="element-268">
          Global Edge Network
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-269">
          {[{
          region: 'North America',
          cities: ['New York', 'Los Angeles', 'Miami']
        }, {
          region: 'Europe',
          cities: ['London', 'Amsterdam', 'Frankfurt']
        }, {
          region: 'Asia Pacific',
          cities: ['Tokyo', 'Singapore', 'Sydney']
        }, {
          region: 'Latin America',
          cities: ['São Paulo', 'Mexico City']
        }].map((region, i) => <div key={i} className="space-y-4" data-id="element-270">
              <h4 className="font-bold uppercase text-[#FFD700] text-sm tracking-widest" data-id="element-271">
                {region.region}
              </h4>
              <ul className="space-y-2" data-id="element-272">
                {region.cities.map((city, j) => <li key={j} className="text-gray-500 text-xs flex items-center justify-center gap-2" data-id="element-273">
                    <div className="w-1 h-1 bg-white/20 rounded-full" data-id="element-274" />
                    {city}
                  </li>)}
              </ul>
            </div>)}
        </div>
        <p className="mt-16 text-sm text-gray-500 font-medium italic" data-id="element-275">
          "Content delivered from the edge, ensuring your fans never wait for a
          page to load."
        </p>
      </section>

      {/* CTA Section */}
      <section className="text-center py-20 border-t border-white/5" data-id="element-276">
        <h3 className="font-['Archivo_Black'] text-2xl md:text-4xl uppercase mb-8" data-id="element-277">
          Choose Your Power Level
        </h3>
        <p className="text-gray-400 mb-12 max-w-2xl mx-auto" data-id="element-278">
          All hosting plans include our full enterprise suite. Select the tier
          that fits your current reach and future goals.
        </p>
        <button onClick={() => setActiveSection?.('pricing')} className="group flex items-center gap-3 bg-white text-black px-12 py-6 rounded-full font-bold uppercase tracking-widest hover:bg-[#FFD700] transition-all shadow-xl mx-auto" data-id="element-279">
          View Hosting Plans
          <ArrowRight className="group-hover:translate-x-2 transition-transform" data-id="element-280" />
        </button>
      </section>
    </div>;
}